import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Package, Plus, X, FloppyDisk, DownloadSimple, Trash, MagnifyingGlass,
  ClockCounterClockwise, FileArrowUp,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Supplier Specifications Console — /assessor/supplier-specs
 *
 *  Per-supplier CoA / spec sheet / sustainability declaration uploads
 *  linked to ingredients in the Aᴳ Masterlist. Auto-versioned per
 *  (supplier × ingredient) pair, with download audit trail.
 * =======================================================================*/

const SPEC_TYPE_LABEL = {
  coa: 'Certificate of Analysis',
  spec_sheet: 'Specification Sheet',
  sustainability_declaration: 'Sustainability Declaration',
  other: 'Other',
};
const SPEC_TYPE_TONE = {
  coa: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]',
  spec_sheet: 'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]',
  sustainability_declaration: 'bg-[#FFE5E7] text-[#9B0014] border-[#C4B5FD]',
  other: 'bg-gray-100 text-[#4B5563] border-gray-300',
};

function UploadForm({ onUploaded, onClose }) {
  const [form, setForm] = useState({
    supplier_name: '', ingredient_inci: '', spec_type: 'coa',
    supplier_country: '', cas_number: '', purity_pct: '', notes: '',
    valid_from: '', valid_until: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!form.supplier_name.trim() || !form.ingredient_inci.trim()) {
      setErr('Supplier and ingredient INCI are required.'); return;
    }
    setErr(''); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      if (file) fd.append('file', file);
      await axios.post(`${API_URL}/api/admin/supplier-specs`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-5 bg-white" data-testid="spec-upload-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#111827] flex items-center gap-2"><FileArrowUp size={18} weight="duotone" className="text-[#E63946]" /> Upload supplier spec</h3>
        <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Supplier *</Label>
          <Input value={form.supplier_name} onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="e.g. Acme Chemicals" data-testid="spec-supplier" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Country</Label>
          <Input value={form.supplier_country} onChange={(e) => setForm(f => ({ ...f, supplier_country: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="Germany" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Ingredient INCI *</Label>
          <Input value={form.ingredient_inci} onChange={(e) => setForm(f => ({ ...f, ingredient_inci: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="Glycerin" data-testid="spec-ingredient" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">CAS number</Label>
          <Input value={form.cas_number} onChange={(e) => setForm(f => ({ ...f, cas_number: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="56-81-5" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Spec type *</Label>
          <select value={form.spec_type} onChange={(e) => setForm(f => ({ ...f, spec_type: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="spec-type">
            {Object.entries(SPEC_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Purity %</Label>
          <Input type="number" step="0.01" value={form.purity_pct} onChange={(e) => setForm(f => ({ ...f, purity_pct: e.target.value }))} className="mt-1 h-9 text-sm tabular-nums" placeholder="99.5" data-testid="spec-purity" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Valid from</Label>
          <Input type="date" value={form.valid_from} onChange={(e) => setForm(f => ({ ...f, valid_from: e.target.value }))} className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Valid until</Label>
          <Input type="date" value={form.valid_until} onChange={(e) => setForm(f => ({ ...f, valid_until: e.target.value }))} className="mt-1 h-9 text-sm" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="text-[12px] mt-1" placeholder="Internal review notes" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Upload file (PDF / DOCX / XLSX / CSV / image · ≤20MB)</Label>
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 h-9 text-sm" data-testid="spec-file" />
        </div>
      </div>
      {err && <p className="text-[11px] text-[#9B1C1C] mt-2" data-testid="spec-upload-error">{err}</p>}
      <Button onClick={submit} disabled={saving || !form.supplier_name.trim() || !form.ingredient_inci.trim()} className="mt-3 h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="spec-upload-save">
        <FloppyDisk size={13} weight="bold" className="mr-1.5" /> {saving ? 'Uploading…' : 'Upload + create version'}
      </Button>
    </div>
  );
}

function DetailPanel({ specId, onAfterAction, onClose }) {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/supplier-specs/${specId}`, { withCredentials: true });
      setSpec(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [specId]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!window.confirm(`Delete v${spec.version} of this spec? File on disk will be removed.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/supplier-specs/${specId}`, { withCredentials: true });
      onAfterAction();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const download = () => {
    window.open(`${API_URL}/api/admin/supplier-specs/${specId}/download`, '_blank');
  };

  if (loading || !spec) return <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm">Loading…</div>;

  return (
    <div className="card-flat p-5 bg-white" data-testid="spec-detail">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{spec.supplier_name}{spec.supplier_country ? ` · ${spec.supplier_country}` : ''}</p>
          <h3 className="text-base font-bold text-[#111827] truncate">{spec.ingredient_inci}</h3>
          <p className="text-[11px] text-[#4B5563]">CAS {spec.cas_number || '—'} · v{spec.version}{spec.is_current ? ' (current)' : ''}</p>
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${SPEC_TYPE_TONE[spec.spec_type] || ''}`}>{SPEC_TYPE_LABEL[spec.spec_type]}</span>
          <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Purity</p><p className="font-semibold tabular-nums">{spec.purity_pct != null ? `${spec.purity_pct}%` : '—'}</p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Uploaded</p><p className="font-semibold">{spec.uploaded_by}<br/><span className="text-[10px] text-[#6B7280]">{new Date(spec.uploaded_at).toLocaleDateString()}</span></p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Valid from</p><p className="font-semibold tabular-nums">{spec.valid_from || '—'}</p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Valid until</p><p className="font-semibold tabular-nums">{spec.valid_until || '—'}</p></div>
      </div>
      {spec.notes && <p className="text-[12px] text-[#4B5563] bg-[#F9FAFB] rounded p-2 mb-3">{spec.notes}</p>}
      <div className="flex gap-2 mb-3">
        {spec.file_name && (
          <Button onClick={download} className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="spec-download"><DownloadSimple size={12} className="mr-1" weight="bold" /> Download {spec.file_name}</Button>
        )}
        <Button onClick={remove} variant="outline" className="h-8 text-xs rounded text-[#9B1C1C] border-[#F8B4B4] hover:bg-[#FDE8E8]" data-testid="spec-delete"><Trash size={12} className="mr-1" /> Delete v{spec.version}</Button>
      </div>
      {err && <p className="text-[11px] text-[#9B1C1C]" data-testid="spec-detail-error">{err}</p>}

      {spec.history?.length > 1 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Version history ({spec.history.length})</p>
          <div className="space-y-1">
            {spec.history.map(h => (
              <div key={h.id} className="text-[11px] text-[#4B5563] flex justify-between items-center bg-[#F9FAFB] px-2 py-1 rounded">
                <span>v{h.version} · {SPEC_TYPE_LABEL[h.spec_type]} · {h.uploaded_by}</span>
                <span className="text-[10px] tabular-nums">{new Date(h.uploaded_at).toLocaleDateString()}{h.is_current ? ' · current' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {spec.audit?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5 flex items-center gap-1"><ClockCounterClockwise size={11} /> Audit ({spec.audit.length})</p>
          <div className="space-y-1">
            {spec.audit.slice().reverse().slice(0, 10).map((a, i) => (
              <div key={i} className="text-[11px] text-[#4B5563] flex justify-between">
                <span><span className="font-mono bg-gray-100 px-1 rounded text-[10px]">{a.action}</span> {a.by}</span>
                <span className="text-[10px] tabular-nums">{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupplierSpecsPage() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [onlyCurrent, setOnlyCurrent] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (typeFilter) params.set('spec_type', typeFilter);
      params.set('only_current', onlyCurrent ? 'true' : 'false');
      params.set('limit', '100');
      const r = await axios.get(`${API_URL}/api/admin/supplier-specs?${params}`, { withCredentials: true });
      setItems(r.data.items || []);
      setCounters(r.data.counters || {});
    } catch (e) { /* keep */ }
  }, [q, typeFilter, onlyCurrent]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Package size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Supplier Specifications</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">CoA · spec sheets · admin only</span>
          </div>
          <p className="text-sm text-[#4B5563]">
            Per-supplier specifications, Certificates of Analysis, and sustainability declarations attached to Aᴳ Masterlist ingredients. Auto-versioned, audit-logged, downloadable.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded">← Assessor Console</Button></Link>
            <Link to="/assessor/hale-verified"><Button variant="outline" className="h-8 text-xs rounded" data-testid="specs-to-verified">Hale Verified →</Button></Link>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-4">
          {[
            { label: 'Total', val: counters.total },
            { label: 'CoA', val: counters.coa },
            { label: 'Spec sheets', val: counters.spec_sheet },
            { label: 'Sustainability', val: counters.sustainability_declaration },
            { label: 'Other', val: counters.other },
            { label: 'Suppliers', val: counters.suppliers_unique },
            { label: 'Ingredients', val: counters.ingredients_covered },
          ].map(c => (
            <div key={c.label} className="p-2 rounded border border-gray-200 bg-white">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{c.label}</p>
              <p className="text-lg font-black tabular-nums text-[#111827]">{c.val ?? 0}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Supplier / INCI / CAS" className="h-9 text-sm pl-8" data-testid="specs-search" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Type</Label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="mt-1 h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="specs-type-filter">
              <option value="">All</option>
              {Object.entries(SPEC_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-[#4B5563] pb-2">
            <input type="checkbox" checked={onlyCurrent} onChange={(e) => setOnlyCurrent(e.target.checked)} data-testid="specs-only-current" />
            Only current versions
          </label>
          <Button onClick={() => { setShowUpload(true); setSelected(null); }} className="h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold ml-auto" data-testid="specs-new"><Plus size={13} weight="bold" className="mr-1" /> Upload spec</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* List */}
          <div className="lg:col-span-2 card-flat bg-white">
            <div className="divide-y divide-gray-100 max-h-[640px] overflow-y-auto">
              {items.length === 0 ? <p className="text-sm text-[#4B5563] py-10 text-center">No specs yet. Click <em>Upload spec</em> to start.</p> :
                items.map(s => (
                  <button key={s.id} onClick={() => { setSelected(s.id); setShowUpload(false); }} className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${selected === s.id ? 'bg-[#FFE9EE]' : ''}`} data-testid={`specs-row-${s.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-[#111827] truncate">{s.ingredient_inci}</p>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${SPEC_TYPE_TONE[s.spec_type]}`}>{s.spec_type}</span>
                    </div>
                    <p className="text-[11px] text-[#4B5563] truncate">{s.supplier_name} · v{s.version}{s.is_current ? ' (current)' : ''}</p>
                    <p className="text-[10px] text-[#6B7280] tabular-nums">{new Date(s.uploaded_at).toLocaleDateString()} · {s.file_name || 'no file'}</p>
                  </button>
                ))
              }
            </div>
          </div>

          {/* Detail / Upload */}
          <div className="lg:col-span-3">
            {showUpload ? (
              <UploadForm onUploaded={() => { setShowUpload(false); load(); }} onClose={() => setShowUpload(false)} />
            ) : selected ? (
              <DetailPanel specId={selected} onAfterAction={() => { setSelected(null); load(); }} onClose={() => setSelected(null)} />
            ) : (
              <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm flex flex-col items-center gap-2">
                <Package size={32} weight="duotone" className="text-[#9CA3AF]" />
                Pick a spec to view or upload a new one.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
